import isNumber from 'lodash/isNumber';
import overSome from 'lodash/overSome';
import toNumber from 'lodash/toNumber';
import mergeWith from 'lodash/mergeWith';
import get from 'lodash/get';
import round from 'lodash/round';

const Modifiers = ['range', 'irregular-range', 'size'];

const Components = {
  fats: 'fats',
  proteins: 'proteins',
  carbs: 'carbs',
  salt: 'salt',
  fibre: 'fibre',
  fatSaturates: 'fatSaturates',
  carbsSugar: 'carbsSugar',
};

const CaloriesRatio = {
  [Components.fats]: 9,
  [Components.proteins]: 4,
  [Components.carbs]: 4,
  [Components.fibre]: 0,
  [Components.salt]: 0,
  [Components.fatSaturates]: 0,
  [Components.carbsSugar]: 0,
};

const Utils = {
  percentage: (x, y) => Math.round((x * 100) / y),
  toPrecision: (value, precision = 10) => toNumber(value.toPrecision(precision)),
  toNumber: value => {
    const number = toNumber(value);
    return Number.isNaN(number) ? null : number;
  },
  hasNutritionComponent: component => nutrition => isNumber(nutrition[component]),
  calculateNutritionComponentWeight: (weight, weightPer100g) => {
    if (isNumber(weightPer100g)) {
      return +((weight * weightPer100g) / 100).toPrecision(10);
    }
    return null;
  },
};

function getItemWeight({ nutritionalMeta, customizations = [] }) {
  const { defaultQuantity } = nutritionalMeta;

  const modifier = customizations.find(({ type }) => Modifiers.includes(type));

  if (modifier) {
    const variation = modifier.variations.find(({ step }) => step === modifier.current);
    if (variation) {
      const unit = get(variation, 'unit.slug') || get(variation, 'unit.unit.slug');
      if (unit === 'weight_g' || unit === 'volume_ml') {
        return Utils.toNumber(variation.value || defaultQuantity);
      }
      if (unit === 'unit') {
        return Utils.toNumber(variation.value ? +variation.value * +defaultQuantity : defaultQuantity);
      }
    }
  }

  return Utils.toNumber(defaultQuantity);
}

function calculateFromNutritionFacts(nutritionalMeta, itemWeight) {
  const nutritionComponents = [Components.fats, Components.carbs, Components.proteins];
  return nutritionComponents.reduce((acc, component) => {
    const nutritionComponentWeightPer100g = nutritionalMeta[component] || 0;
    const nutritionComponentWeight = Utils.calculateNutritionComponentWeight(
      itemWeight,
      nutritionComponentWeightPer100g
    );
    const componentCalories = Utils.toPrecision(nutritionComponentWeight * CaloriesRatio[component]);
    return Utils.toPrecision(acc + componentCalories);
  }, 0);
}

function calculateFromCalories({ calories }, itemWeight) {
  /* totalCalories = itemWeight * calories per 100g / 100g */
  return Utils.toPrecision((itemWeight * calories) / 100);
}

const hasAnyOfNutritionComponents = overSome([
  Utils.hasNutritionComponent(Components.fats),
  Utils.hasNutritionComponent(Components.proteins),
  Utils.hasNutritionComponent(Components.carbs),
]);

function getNutritionInfoOverride(nutritionalInfo, size) {
  const { overrides, nutrition } = nutritionalInfo;
  const override = overrides.find(override => override.context_value === size);
  return (override ? override.nutrition : nutrition) || {};
}

function getCustomizationsNutritionalInfo(item, size) {
  return item.customizations.reduce((acc, customization) => {
    const variation = customization.variations.find(({ step }) => step === customization.current);
    const { nutritionalInfo } = variation || {};
    if (nutritionalInfo) {
      const nutrition = getNutritionInfoOverride(nutritionalInfo, size);
      return sumObjects(acc, nutrition);
    }
    return acc;
  }, {});
}

function getCurrentSize(item) {
  const { customizations } = item;
  const size = customizations?.find(({ type }) => type === 'size');
  const currentVariation = size?.variations.find(({ step }) => step === size.current);
  return currentVariation?.parentUUID;
}

function getCaloriesByNutritionalInfo(item) {
  const size = getCurrentSize(item);
  const nutrition = getNutritionInfoOverride(item.nutritionalInfo, size);
  const customizationsNutrition = getCustomizationsNutritionalInfo(item, size);
  return (nutrition?.calories || 0) + (customizationsNutrition?.calories || 0);
}

function calculateItemCalories(item) {
  const { nutritionalMeta, nutritionalInfo } = item;
  if (nutritionalInfo) return getCaloriesByNutritionalInfo(item);
  if (!nutritionalMeta) {
    return null;
  }
  const weight = getItemWeight(item);
  if (weight === null) {
    return null;
  }
  if (isNumber(nutritionalMeta.calories)) {
    return round(calculateFromCalories(nutritionalMeta, weight));
  }
  if (hasAnyOfNutritionComponents(nutritionalMeta)) {
    return round(calculateFromNutritionFacts(nutritionalMeta, weight));
  }
  return null;
}

function calculateNutritionComponentsWeights(item) {
  const { nutritionalMeta, nutritionalInfo } = item;
  if (nutritionalInfo) {
    const size = getCurrentSize(item);
    const nutrition = sumObjects(
      getNutritionInfoOverride(item.nutritionalInfo, size),
      getCustomizationsNutritionalInfo(item, size)
    );
    if (hasAnyOfNutritionComponents(nutrition)) {
      return nutrition;
    }
  }
  if (nutritionalMeta) {
    const weight = getItemWeight(item);
    if (hasAnyOfNutritionComponents(nutritionalMeta)) {
      return {
        [Components.fats]: Utils.calculateNutritionComponentWeight(weight, nutritionalMeta.fats),
        [Components.carbs]: Utils.calculateNutritionComponentWeight(weight, nutritionalMeta.carbs),
        [Components.proteins]: Utils.calculateNutritionComponentWeight(weight, nutritionalMeta.proteins),
        [Components.salt]: Utils.calculateNutritionComponentWeight(weight, nutritionalMeta.salt),
        [Components.fatSaturates]: Utils.calculateNutritionComponentWeight(weight, nutritionalMeta.fatSaturates),
        [Components.carbsSugar]: Utils.calculateNutritionComponentWeight(weight, nutritionalMeta.carbsSugar),
        [Components.fibre]: Utils.calculateNutritionComponentWeight(weight, nutritionalMeta.fibre),
      };
    }
  }
  return {
    fats: null,
    carbs: null,
    proteins: null,
    salt: null,
    fatSaturates: null,
    carbsSugar: null,
  };
}

function calculateBundleCalories(items) {
  const calories = items.reduce((acc, item) => {
    const itemCalories = calculateItemCalories(item);
    if (!isNumber(itemCalories)) {
      return acc;
    }

    return Utils.toPrecision(acc + itemCalories);
  }, null);
  return { calories: isNumber(calories) ? Math.round(calories) : null };
}

// @TODO: remove this when we'll refuse meal v1
function calculateBundleNutritionComponentsWeights(items) {
  return calculateBundleNutritionWeights(items, true);
}

function calculateBundleNutritionWeights(items, round = false) {
  return items.reduce(
    (acc, item) => {
      const itemNutrition = calculateNutritionComponentsWeights(item);
      return sumObjects(itemNutrition, acc, round);
    },
    {
      [Components.fats]: null,
      [Components.carbs]: null,
      [Components.proteins]: null,
      [Components.salt]: null,
      [Components.fatSaturates]: null,
      [Components.carbsSugar]: null,
      [Components.fibre]: null,
    }
  );
}

function formatNutritionComponent(value) {
  return isNumber(value) ? Math.round(value) : '--';
}

function calculateNutritionComponentsPercentage(nutrition) {
  const { carbs = 0, fats = 0, proteins = 0 } = nutrition;

  const proteinsPart = CaloriesRatio[Components.proteins] * proteins;
  const fatsPart = CaloriesRatio[Components.fats] * fats;
  const carbsPart = CaloriesRatio[Components.carbs] * carbs;
  const nutritionSum = proteinsPart + fatsPart + carbsPart;

  const carbsPercentage = round((carbsPart / nutritionSum) * 100);
  const fatsPercentage = round((fatsPart / nutritionSum) * 100);

  // Last nutrition component is calculated this way to make sure,
  // that component's sum will always be equal to 100%
  const proteinsPercentage = round(100 - carbsPercentage - fatsPercentage);

  return {
    carbsPercentage,
    fatsPercentage,
    proteinsPercentage,
  };
}

function sumObjects(a, b, round = false) {
  return mergeWith({}, a, b, (aValue, bValue) => {
    if (isNumber(aValue)) {
      const sum = Utils.toPrecision(aValue + bValue);
      return round ? Math.round(sum) : sum;
    }
    return bValue;
  });
}

export const NutritionCalculator = {
  Components,
  Utils,
  getItemWeight,
  calculateFromNutritionFacts,
  calculateFromCalories,
  calculateItemCalories,
  calculateNutritionComponentsWeights,
  calculateBundleCalories,
  calculateBundleNutritionComponentsWeights,
  calculateBundleNutritionWeights,
  formatNutritionComponent,
  calculateNutritionComponentsPercentage,
};
